if (!customElements.get('sticky-atc')) {
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productInfo = document.querySelector(`product-info#MainProduct-${this.sectionId}`);

      // Watch the INFO COLUMN, not the full product-info wrapper
      this.triggerEl =
        (this.productInfo && this.productInfo.querySelector('.product__info-wrapper')) ||
        (this.productInfo && this.productInfo.querySelector('.product__info-container')) ||
        document.querySelector('.product__info-wrapper') ||
        document.querySelector('.product__info-container') ||
        this.productInfo;

      this.variantSelect = this.querySelector('[data-sticky-variant-select]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.addButton = this.querySelector('[data-sticky-add-button]');
      this.buttonText = this.querySelector('[data-sticky-button-text]');
      this.quantityInput = this.querySelector('[data-sticky-quantity-input]');
      this.quantityMinus = this.querySelector('[data-sticky-quantity-minus]');
      this.quantityPlus = this.querySelector('[data-sticky-quantity-plus]');
      this.sectionHeader = document.querySelector('.section-header');

      if (!this.triggerEl) {
        console.warn('Sticky ATC: no trigger element found');
        return;
      }

      // Start explicitly hidden — prevents any flash before observer fires
      this.dataset.visible = 'false';
      this.removeAttribute('hidden');

      this.setupHeaderOffsetSync();
      this.setupVisibilityLogic();
      this.setupVariantSync();
      this.setupQuantityStepper();
      this.setupAddToCart();
      this.listenForMainFormChanges();

      // Run an immediate check so initial state is correct
      this.evaluateVisibility();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this.headerObserver) this.headerObserver.disconnect();
      if (this.boundScrollHandler) {
        window.removeEventListener('scroll', this.boundScrollHandler);
        window.removeEventListener('resize', this.boundScrollHandler);
      }
    }

    getHeaderBottom() {
      if (!this.sectionHeader) return 0;

      const isHidden = this.sectionHeader.classList.contains(
        'shopify-section-header-hidden'
      );
      if (isHidden) return 0;

      const rect = this.sectionHeader.getBoundingClientRect();
      return Math.max(0, rect.bottom);
    }

    /**
     * Position the bar directly under whatever's currently at the top of the
     * viewport (announcement bar + header, or just the sticky header, etc).
     */
    setupHeaderOffsetSync() {
      const updateOffset = () => {
        const offset = this.getHeaderBottom();
        this.style.setProperty('--sticky-atc-top-offset', `${offset}px`);
      };

      updateOffset();

      this.boundScrollHandler = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          updateOffset();
          this.evaluateVisibility();
          this.scrollFrame = null;
        });
      };

      window.addEventListener('scroll', this.boundScrollHandler, { passive: true });
      window.addEventListener('resize', this.boundScrollHandler);

      if (this.sectionHeader) {
        this.headerObserver = new MutationObserver(() => {
          updateOffset();
          this.evaluateVisibility();
        });
        this.headerObserver.observe(this.sectionHeader, {
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      }
    }

    /**
     * Show the bar ONLY when the info column has scrolled fully above the
     * header's bottom edge. Hide it the moment the info column re-enters
     * (i.e. user scrolls back up).
     */
    evaluateVisibility() {
      if (!this.triggerEl) return;

      const triggerRect = this.triggerEl.getBoundingClientRect();
      const headerBottom = this.getHeaderBottom();

      // The info column's bottom must be ABOVE the header's bottom
      const scrolledPast = triggerRect.bottom < headerBottom;

      // Safety: never show if the trigger element hasn't even been reached
      const triggerInOrBelowViewport = triggerRect.top < window.innerHeight;

      const shouldShow = scrolledPast && triggerInOrBelowViewport;
      this.dataset.visible = shouldShow ? 'true' : 'false';
    }

    setupVisibilityLogic() {
      this.observer = new IntersectionObserver(
        () => this.evaluateVisibility(),
        { threshold: [0, 0.5, 1] }
      );
      this.observer.observe(this.triggerEl);
    }

    getMainVariantInput() {
      const form = document.getElementById(this.dataset.productFormId);
      if (!form) return null;
      return form.querySelector('input[name="id"]');
    }

    /**
     * Read the quantity. Prefer the sticky bar's own input; fall back to the
     * main product form's quantity input if for some reason ours is missing.
     */
    getMainQuantity() {
      if (this.quantityInput) {
        const val = parseInt(this.quantityInput.value, 10);
        if (!Number.isNaN(val) && val >= 1) return val;
      }

      const form = document.getElementById(this.dataset.productFormId);
      let qtyInput = form ? form.querySelector('input[name="quantity"]') : null;

      if (!qtyInput && this.productInfo) {
        qtyInput =
          this.productInfo.querySelector('quantity-input input[name="quantity"]') ||
          this.productInfo.querySelector('input[name="quantity"]');
      }

      const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      return Number.isNaN(qty) || qty < 1 ? 1 : qty;
    }

    /**
     * Resolve the variant id from the most reliable source available:
     *   1. The sticky dropdown (multi-variant products)
     *   2. The hidden input[name="id"] in the main product form
     *   3. The Liquid-rendered fallback (single-variant products, or when the
     *      form's hidden input isn't present yet)
     */
    resolveVariantId() {
      if (this.variantSelect && this.variantSelect.value) {
        return this.variantSelect.value;
      }

      const mainInput = this.getMainVariantInput();
      if (mainInput && mainInput.value) {
        return mainInput.value;
      }

      if (this.dataset.currentVariantId) {
        return this.dataset.currentVariantId;
      }

      return null;
    }

    setupVariantSync() {
      if (!this.variantSelect) return;

      this.variantSelect.addEventListener('change', (e) => {
        const variantId = e.target.value;
        if (!this.productInfo) return;

        const mainVariantInput = this.getMainVariantInput();
        if (mainVariantInput) mainVariantInput.value = variantId;

        const variantPicker = this.productInfo.querySelector(
          'variant-radios, variant-selects'
        );
        if (variantPicker) {
          const matchingInput = variantPicker.querySelector(`[value="${variantId}"]`);
          if (matchingInput) {
            matchingInput.checked = true;
            matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }

    setupQuantityStepper() {
      if (!this.quantityInput) return;

      const clamp = () => {
        let val = parseInt(this.quantityInput.value, 10);
        const min = parseInt(this.quantityInput.min, 10) || 1;
        if (Number.isNaN(val) || val < min) val = min;
        this.quantityInput.value = val;
        return val;
      };

      if (this.quantityMinus) {
        this.quantityMinus.addEventListener('click', () => {
          const min = parseInt(this.quantityInput.min, 10) || 1;
          const val = clamp();
          this.quantityInput.value = Math.max(min, val - 1);
        });
      }

      if (this.quantityPlus) {
        this.quantityPlus.addEventListener('click', () => {
          const val = clamp();
          this.quantityInput.value = val + 1;
        });
      }

      this.quantityInput.addEventListener('change', clamp);
      this.quantityInput.addEventListener('blur', clamp);
    }

    setupAddToCart() {
      if (!this.addButton) return;

      this.addButton.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.addButton.disabled || this.addButton.getAttribute('aria-busy') === 'true') return;

        const variantId = this.resolveVariantId();

        if (!variantId) {
          console.warn('Sticky ATC: no variant id available');
          return;
        }

        const quantity = this.getMainQuantity();

        this.addButton.setAttribute('aria-busy', 'true');

        try {
          await this.addToCart(variantId, quantity);
        } catch (err) {
          console.error('Sticky ATC: add to cart failed', err);
        } finally {
          this.addButton.setAttribute('aria-busy', 'false');
        }
      });
    }

    async addToCart(variantId, quantity = 1) {
      const cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      const formData = {
        items: [{ id: parseInt(variantId, 10), quantity: quantity }],
      };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.sections = cart.getSectionsToRender().map((section) => section.id);
        formData.sections_url = window.location.pathname;
      }

      const response = await fetch(`${window.routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(data);
      } else {
        window.location.href = window.routes.cart_url || '/cart';
      }

      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(
          data.sections['cart-icon-bubble'],
          'text/html'
        );
        const newBubble = newDoc.querySelector('#cart-icon-bubble');
        if (newBubble) cartIconBubble.innerHTML = newBubble.innerHTML;
      }
    }

    listenForMainFormChanges() {
      const mainVariantInput = this.getMainVariantInput();
      if (!mainVariantInput) return;

      const sync = () => {
        const newId = mainVariantInput.value;
        if (this.variantSelect && this.variantSelect.value !== newId) {
          this.variantSelect.value = newId;
        }
        // Keep the Liquid fallback in sync too, so resolveVariantId() always
        // returns the currently-selected variant even via the dataset path.
        if (newId) this.dataset.currentVariantId = newId;
        this.updatePriceAndAvailability(newId);
      };

      this.mutationObserver = new MutationObserver(sync);
      this.mutationObserver.observe(mainVariantInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }

    async updatePriceAndAvailability(variantId) {
      try {
        const url = `${window.location.pathname}?variant=${variantId}&section_id=${this.sectionId}`;
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newPrice = doc.querySelector('[data-sticky-price]');
        const newButton = doc.querySelector('[data-sticky-add-button]');

        if (newPrice && this.priceEl) {
          this.priceEl.innerHTML = newPrice.innerHTML;
        }
        if (newButton && this.addButton) {
          this.addButton.disabled = newButton.disabled;
          const newText = newButton.querySelector('[data-sticky-button-text]');
          if (newText && this.buttonText) {
            this.buttonText.innerHTML = newText.innerHTML;
          }
        }
      } catch (err) {
        console.error('Sticky ATC update failed', err);
      }
    }
  }

  customElements.define('sticky-atc', StickyAtc);
}